import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter } from 'lucide-react';
import WhatsAppFloatingButton from "../pages/WhatsAppFloatingButton";
import { INSIGHT_API, SUBSCRIBE_API } from '@/data/mockData'; // Add your /subscribe endpoint here
import { Oval } from 'react-loader-spinner';

const InsightsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe state
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(INSIGHT_API)
      .then((res) => res.json())
      .then((data) => {
        setInsights(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const categories = ['All', ...new Set(insights.map((i) => i.category))];

  const filteredInsights =
    selectedCategory === 'All'
      ? insights
      : insights.filter((i) => i.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  // Subscribe handler
  const handleSubscribe = async () => {
    if (!subscribeEmail || !/\S+@\S+\.\S+/.test(subscribeEmail)) {
      setSubscribeStatus('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setSubscribeStatus('');

    try {
      const response = await fetch(SUBSCRIBE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: subscribeEmail }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSubscribeStatus('Subscribed successfully!');
        setSubscribeEmail('');
      } else {
        setSubscribeStatus(data.message || 'Subscription failed.');
      }
    } catch (err) {
      console.error(err);
      setSubscribeStatus('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-ice-blue relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&q=80"
            alt="Analytics"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-3xl"
          >
            <motion.span
              variants={itemVariants}
              className="service-tag bg-royal-blue text-white"
            >
              Industry Insights
            </motion.span>
            <motion.h1
              variants={itemVariants}
              className="text-5xl md:text-6xl font-bold mt-6 mb-8 text-navy"
            >
              Latest <span className="text-royal-blue">Insights</span>
            </motion.h1>
            <motion.p
              variants={itemVariants}
              className="text-xl text-steel-blue leading-relaxed"
            >
              Timely, sharp takes on capital flows, family office trends, and
              emerging investment themes—crafted for decision-makers. Stay ahead
              with curated perspectives that decode the market, not just report
              it.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center space-x-4 overflow-x-auto pb-2">
            <Filter className="w-5 h-5 text-silver flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`service-tag whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-royal-blue text-white'
                    : 'bg-gray-100 text-slate-blue hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <WhatsAppFloatingButton />

      {/* Insights Grid */}
      <div
  key={selectedCategory}
  initial="hidden"
  animate="visible"
  exit="hidden"
  variants={containerVariants}
  className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
>
  {filteredInsights.map((insight) => (
    <a
      key={insight.id}
      href={insight.link}
      target="_blank"
      rel="noopener noreferrer"
      variants={itemVariants}
      layout
      className="group block rounded-lg overflow-hidden card-hover bg-snow border border-gray-100"
    >
      <div className="aspect-video overflow-hidden">
        <img
          src={insight.image}
          alt={insight.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <span className="service-tag bg-light-blue text-royal-blue mb-3">
          {insight.category}
        </span>
        <h3 className="font-bold text-lg text-navy group-hover:text-royal-blue transition-colors">
          {insight.title}
        </h3>
        <p className="text-silver text-sm mt-2">{insight.date}</p>
        <span className="inline-flex items-center text-royal-blue text-sm font-semibold mt-4 group-hover:underline">
          Read More <ArrowRight className="w-3 h-3 ml-1" />
        </span>
      </div>
    </a>
  ))}
</div>


      {/* Newsletter CTA */}
      <section className="py-24 lg:py-32 bg-snow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="text-center max-w-2xl mx-auto"
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold mb-6 text-navy"
            >
              Stay Informed
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-steel-blue text-lg mb-8"
            >
              Get the latest insights on fintech trends, capital flows, and
              investment themes delivered to your inbox.
            </motion.p>
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <input
                type="email"
                placeholder="Enter your email"
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                className="px-5 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 flex-grow max-w-md"
              />
              <button
                onClick={handleSubscribe}
                disabled={submitting}
                className="cta-button"
              >
                {submitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </motion.div>
            {subscribeStatus && (
              <p className="text-center mt-4 text-sm text-royal-blue">
                {subscribeStatus}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-royal-blue">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
            >
              Ready to Build Influence?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-blue-100 text-lg max-w-2xl mx-auto mb-8"
            >
              Let's discuss how we can help elevate your brand.
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link
                to="/contact"
                className="cta-button !bg-white !text-royal-blue hover:!bg-gray-100"
              >
                Let's Connect
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
